import type {Dispatch, ThunkAction} from 'src/reducers';
import {thunkify as flow} from 'src/utils/thunks';
import {key, cached} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import type {LanguageTranslationKeyAndValue} from 'src/types/messages';
import logger from 'src/utils/logger';


export type LanguageTranslationAction =
  | ReceiveAllSupportedLanguages
  | SetCandidateLangCode;

export const RECEIVE_ALL_SUPPORTED_LANGUAGES =
  'messages/language-translation/receive-all-supported-languages';

export const SET_CANDIDATE_LANG_CODE =
  'messages/language-translation/set-candidate-lang-code';

type ReceiveAllSupportedLanguages = {
  type: typeof RECEIVE_ALL_SUPPORTED_LANGUAGES;
  payload: LanguageTranslationKeyAndValue;
};

export const receiveAllSupportedLanguages = (
  payload: LanguageTranslationKeyAndValue,
): ReceiveAllSupportedLanguages => ({
  type: RECEIVE_ALL_SUPPORTED_LANGUAGES,
  payload,
});

type SetCandidateLangCode = {
  type: typeof SET_CANDIDATE_LANG_CODE;
  payload: {
    id: string;
    langCode: string;
  };
};

export const setCandidateLangCode = (payload: {
  id: string;
  langCode: string;
}): SetCandidateLangCode => ({
  type: SET_CANDIDATE_LANG_CODE,
  payload,
});

export const getAllSupportedLanguages: () => ThunkAction<unknown> = flow(
  key(() => 'messages/language-translation/getSupportedLanguages'),
  cached(
    (response) => {
      return receiveAllSupportedLanguages(response);
    },
    {
      ttl: 60 * 60 * 1000,
    },
  ),
)(
  () =>
    (dispatch: Dispatch): Promise<Response> =>
      dispatch(reduxApi.get('messages_v2/language/get-languages')),
);

export const setCandidateLanguage =
  (inboxId: string, externalId: string, languageCode: string): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.put('messages_v2/language/entity/set-language', {
          external_id: externalId, // phone number or conversation id
          language: languageCode,
          inbox_id: inboxId,
        }),
      );
      dispatch(setCandidateLangCode({id: externalId, langCode: languageCode}));
    } catch (err: any) {
      logger.error('Error in setting candidate language', err);
    }
  };
